interface TelegramBackButton {
  show(): void;
  hide(): void;
  onClick(callback: () => void): void;
  offClick(callback: () => void): void;
}

interface TelegramWebApp {
  initData: string;
  colorScheme: "light" | "dark";
  BackButton: TelegramBackButton;
  ready(): void;
  expand(): void;
  openLink(url: string): void;
  openTelegramLink?(url: string): void;
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function telegramApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

export function telegramLaunchParams(
  searchValue = window.location.search,
  hashValue = window.location.hash,
): URLSearchParams {
  const search = new URLSearchParams(searchValue);
  const hash = hashValue.startsWith("#")
    ? new URLSearchParams(hashValue.slice(1))
    : new URLSearchParams();
  for (const [key, value] of hash) {
    if (!search.has(key)) search.set(key, value);
  }
  return search;
}

export function telegramInitData(): string {
  return telegramApp()?.initData || telegramLaunchParams().get("tgWebAppData") || "";
}

export function isTelegramLaunch(
  searchValue = window.location.search,
  hashValue = window.location.hash,
): boolean {
  const params = telegramLaunchParams(searchValue, hashValue);
  return params.has("tgWebAppData") || params.has("tgWebAppVersion");
}

async function loadTelegramSdk(): Promise<void> {
  if (telegramApp() || !isTelegramLaunch()) return;
  await new Promise<void>((resolve) => {
    const script = document.createElement("script");
    const timeout = window.setTimeout(resolve, 5000);
    script.src = "https://telegram.org/js/telegram-web-app.js?63";
    script.async = true;
    script.onload = () => {
      window.clearTimeout(timeout);
      resolve();
    };
    script.onerror = () => {
      window.clearTimeout(timeout);
      resolve();
    };
    document.head.appendChild(script);
  });
}

export async function initializeTelegram(): Promise<void> {
  await loadTelegramSdk();
  const app = telegramApp();
  if (!app) return;
  document.documentElement.dataset.telegram = "true";
  document.documentElement.dataset.theme = app.colorScheme;
  app.ready();
  app.expand();
}

export function setTelegramBackHandler(handler: (() => void) | null): () => void {
  const button = telegramApp()?.BackButton;
  if (!button) return () => undefined;
  if (!handler) {
    button.hide();
    return () => undefined;
  }
  button.show();
  button.onClick(handler);
  return () => {
    button.offClick(handler);
    button.hide();
  };
}

export function openExternal(url: string): void {
  const app = telegramApp();
  if (app) {
    app.openLink(url);
  } else {
    window.open(url, "_blank", "noopener,noreferrer");
  }
}

export function openBotGame(username: string, gameId: number): void {
  const cleanUsername = username.replace(/^@/, "");
  const url = `https://t.me/${cleanUsername}?start=game_${gameId}`;
  const app = telegramApp();
  if (app?.openTelegramLink) {
    app.openTelegramLink(url);
  } else {
    openExternal(url);
  }
}
